import { Edit } from '@mui/icons-material';
import Delete from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
// import { Box } from '@mui/system';
import Plus from '@untitled-ui/icons-react/build/esm/Plus';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Field } from 'src/API';
import $log from 'src/log/log';
import { DynamicTableData, ValueElement } from '../../FormView';

interface ColumnConfiguration {
  id: string;
  label: string;
  type: 'string' | 'number' | 'text';
  multiline?: boolean;
}

interface NewDataFormat {
  value: DynamicTableData[];
  columns: ColumnConfiguration[];
}

const isOldFormat = (data: DynamicTableData[] | NewDataFormat): data is DynamicTableData[] => {
  return Array.isArray(data);
};

const DynamicTable: React.FC<{
  field: Field;
  data: DynamicTableData[] | NewDataFormat;
  onChange?: (data: NewDataFormat) => void;
  enableAddRows?: boolean;
  enableAddColumns?: boolean;
  enableEdit?: boolean;
}> = ({ field: orgField, data: initData, onChange, enableAddColumns, enableAddRows, enableEdit }) => {
  const field = {
    ...orgField,
    dataSource: JSON.parse(orgField.dataSource || '{}'),
  };
  $log.debug('DynamicTable', { field, initData, rows: field.dataSource.rows });

  const initialData = isOldFormat(initData)
    ? {
        value: initData.length === 0 ? field.dataSource.rows : initData,
        columns: field.dataSource.columns,
      }
    : initData;
  $log.debug('DynamicTable', { initialData });
  const [data, setData] = useState<NewDataFormat>(initialData);
  const [editMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newColumnType, setNewColumnType] = useState<'string' | 'number'>('string');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameColumn, setRenameColumn] = useState<ColumnConfiguration | null>(null);
  const theme = useTheme();

  // $log.debug('DynamicTable', { field, initialData, initData, data, columns });

  useEffect(() => {
    const transformedData = isOldFormat(initData)
      ? {
          value: initData.length === 0 ? field.dataSource.rows : initData,
          columns: field.dataSource.columns,
        }
      : initData;
    if (!_.isEqual(data, transformedData)) {
      setData(transformedData);
    }
  }, [initData]);

  const handleChange = (rowId: string, columnId: string, value: number | string) => {
    setData((prevData) => {
      const updatedRows = [...prevData.value];
      const rowIndex = updatedRows.findIndex((row) => row.id === rowId);

      if (rowIndex === -1) return prevData;

      const row = updatedRows[rowIndex];
      const valueIndex = row.values.findIndex((val) => val.columnId === columnId);

      if (valueIndex !== -1) {
        row.values[valueIndex].value = value;
      } else {
        row.values.push({ columnId, value });
      }

      const updatedData: NewDataFormat = { ...prevData, value: updatedRows };
      onChange?.(updatedData);
      return updatedData;
    });
  };

  const openRenameDialog = (column: ColumnConfiguration) => {
    setRenameColumn(column);
    setRenameDialogOpen(true);
  };

  const handleRenameColumn = () => {
    if (renameColumn) {
      setData((prevData) => {
        const updatedColumns = prevData.columns.map((col) =>
          col.id === renameColumn.id ? { ...col, label: renameColumn.label } : col,
        );
        const updatedData = { ...prevData, columns: updatedColumns };
        onChange?.(updatedData);
        return updatedData;
      });
      setRenameDialogOpen(false);
    }
  };

  const handleAddRow = () => {
    const newRow: DynamicTableData = {
      id: `row-${Date.now()}`,
      values: data.columns.map((col) => ({ columnId: col.id, value: '' })),
    };

    setData((prevData) => {
      const updatedData = { ...prevData, value: [...prevData.value, newRow] };
      onChange?.(updatedData);
      return updatedData;
    });
  };

  const handleAddColumn = () => {
    const newColumn: ColumnConfiguration = {
      id: `col-${Date.now()}`,
      label: `Column ${data.columns.length + 1}`,
      type: newColumnType,
    };

    setData((prevData) => {
      const updatedData = {
        ...prevData,
        columns: [...prevData.columns, newColumn],
        value: prevData.value.map((row) => ({
          ...row,
          values: [...row.values, { columnId: newColumn.id, value: '' }],
        })),
      };
      onChange?.(updatedData);
      return updatedData;
    });

    setDialogOpen(false); // Close dialog
  };

  const handleDeleteRow = (rowId: string) => {
    setData((prevData) => {
      const updatedData = {
        ...prevData,
        value: prevData.value.filter((row) => row.id !== rowId),
      };
      onChange?.(updatedData);
      return updatedData;
    });
  };

  const handleDeleteColumn = (columnId: string) => {
    setData((prevData) => {
      const updatedData: NewDataFormat = {
        columns: prevData.columns.filter((col) => col.id !== columnId),
        value: prevData.value.map((row) => ({
          ...row,
          values: row.values.filter((val) => val.columnId !== columnId),
        })),
      };
      onChange?.(updatedData);
      return updatedData;
    });
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setData((prevData) => {
      const reorderedColumns = [...prevData.columns];
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);

      const updatedData = { ...prevData, columns: reorderedColumns };
      onChange?.(updatedData);
      return updatedData;
    });
  };

  const renderInputField = (column: ColumnConfiguration, rowValue: ValueElement, rowId: string) => {
    if (!column) return null;

    switch (column.type) {
      case 'text':
        return <Typography>{rowValue.value}</Typography>;
      case 'string':
        return (
          <TextField
            type="text"
            value={rowValue.value}
            onChange={(e) => handleChange(rowId, column.id, e.target.value)}
            variant="outlined"
            // multiline={column.multiline || rowValue.multiline }
            multiline={true}
            fullWidth
          />
        );
      case 'number':
        return (
          <TextField
            type="number"
            value={rowValue.value}
            onChange={(e) => handleChange(rowId, column.id, e.target.value)}
            variant="outlined"
            fullWidth
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="columns" direction="horizontal" type="COLUMN">
          {(provided) => (
            <TableContainer component={Paper} {...provided.droppableProps} ref={provided.innerRef}>
              <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} p={2}>
                <Typography variant="h6">{field.label}</Typography>
                <Stack justifySelf={'flex-end'} direction={'row'} alignItems={'center'}>
                  {/* <Box
                                        justifySelf={'flex-end'}
                                    > */}
                  <Typography variant="caption" color="textSecondary">
                    You can modify the table by adding, removing, or renaming columns and rows.
                  </Typography>
                  {enableEdit && (
                    <IconButton
                      onClick={() => setEditMode(!editMode)}
                      // style={{ marginBottom: '10px' }}
                      color="primary"
                      title="Edit table"
                    >
                      <Edit />
                    </IconButton>
                  )}
                  {/* </Box> */}
                </Stack>
              </Stack>
              <Table>
                <TableHead>
                  <TableRow>
                    {data.columns.map((column, index) => (
                      <Draggable key={column.id} draggableId={column.id} index={index} isDragDisabled={!editMode}>
                        {(provided) => (
                          <TableCell
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            key={column.id}
                            style={
                              editMode
                                ? {
                                    ...provided.draggableProps.style,
                                    // border: '1px solid',
                                    borderColor: theme.palette.primary.alpha30,
                                    borderRight: '1px solid',
                                    marginRight: '5px',
                                    // backgroundColor: theme.palette.primary.alpha30,
                                    cursor: editMode ? 'grab' : 'default',
                                  }
                                : undefined
                            }
                          >
                            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                              <Typography variant="caption">{column.label}</Typography> {/* Label on the left */}
                              <Box>
                                {editMode && (
                                  <IconButton onClick={() => openRenameDialog(column)} title="Rename Column">
                                    <Edit />
                                  </IconButton>
                                )}
                                {editMode && enableAddColumns && (
                                  <IconButton
                                    color="error"
                                    title="Delete Column"
                                    onClick={() => handleDeleteColumn(column.id)}
                                  >
                                    <Delete />
                                  </IconButton>
                                )}
                              </Box>
                            </Stack>
                          </TableCell>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {editMode && enableAddColumns && (
                      <TableCell>
                        <IconButton title="Add Column" onClick={() => setDialogOpen(true)}>
                          <Plus />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.value.map((row) => (
                    <TableRow key={row.id}>
                      {data.columns.map((column) => {
                        const rowValue = row.values.find((val) => val.columnId === column.id) || {
                          columnId: column.id,
                          value: '',
                        };
                        $log.debug('DynamicTable', { row });
                        return <TableCell key={column.id}>{renderInputField(column, rowValue, row.id)}</TableCell>;
                      })}
                      {editMode && enableAddColumns && (
                        <TableCell>
                          <IconButton color="error" onClick={() => handleDeleteRow(row.id)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {editMode && enableAddRows && (
                    <TableRow>
                      <TableCell colSpan={data.columns.length + 1}>
                        <IconButton title="Add Row" onClick={handleAddRow}>
                          <Plus />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Droppable>
      </DragDropContext>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Add Column</DialogTitle>
        <DialogContent>
          <Typography>Column Type</Typography>
          <Select
            value={newColumnType}
            onChange={(e) => setNewColumnType(e.target.value as 'string' | 'number')}
            fullWidth
          >
            <MenuItem value="string">Text</MenuItem>
            <MenuItem value="number">Number</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button title="Add Column" onClick={handleAddColumn} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename Column</DialogTitle>
        <DialogContent>
          <TextField
            label="Column Name"
            value={renameColumn?.label || ''}
            onChange={(e) => setRenameColumn((prev) => (prev ? { ...prev, label: e.target.value } : null))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameColumn} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DynamicTable;
